import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money } from "../lib/store";
import { scopeCheck } from "../lib/ai";
import { relative } from "../lib/format";
import type { Project, ScopeFlag } from "../lib/types";

/* Scope-creep sentinel — reads every client request against the agreed scope and
   flags the ones that are chargeable extras, ready to raise as a variation. */

function agreedScope(p: Project): string {
  const L: string[] = [];
  L.push(`Project: ${p.name} — ${p.type}${p.location ? `, ${p.location}` : ""}.`);
  for (const s of (p.brief?.sections ?? [])) if (s.body?.trim()) L.push(`Brief — ${s.title}: ${s.body.slice(0, 700)}`);
  const spaces = (p.brief as any)?.spaces ?? [];
  if (spaces.length) L.push(`Agreed accommodation: ${spaces.map((s: any) => `${s.name} ×${s.qty}`).join("; ")}`);
  const b = p.billing;
  if (b) {
    L.push(`Agreed fee: ${b.contractSum} ${b.currency}.`);
    if (b.disciplines?.length) L.push(`Services by discipline: ${b.disciplines.map((d) => d.name).join(", ")}.`);
    if (b.stages?.length) L.push(`Fee stages: ${b.stages.map((s: any) => s.label).join(", ")}.`);
    if (b.additionalServices?.length) L.push(`Chargeable extras named in the agreement: ${b.additionalServices.map((s) => `${s.label} (${s.rate}${s.unit ? `/${s.unit}` : ""})`).join("; ")}.`);
    if (b.exclusions?.length) L.push(`Explicit exclusions: ${b.exclusions.join("; ")}.`);
  }
  const eng = p.engagement;
  if (eng?.limitations?.length) L.push(`Limitations of service: ${eng.limitations.join("; ")}`);
  return L.join("\n");
}

/** Every client-originated request worth judging. */
function gatherRequests(p: Project) {
  const out: { source: "review" | "message" | "rfi"; ref?: string; text: string }[] = [];
  for (const r of (p.reviews ?? [])) for (const s of r.sheets) for (const m of s.remarks) if (m.author === "client") out.push({ source: "review", ref: s.code, text: m.body });
  for (const f of (p.feedback ?? [])) if (f.author === "client") out.push({ source: "message", ref: f.context, text: f.body });
  for (const rf of (p.rfis ?? [])) if (rf.raisedByRole === "client") out.push({ source: "rfi", ref: rf.ref, text: `${rf.subject} — ${rf.question}` });
  return out;
}

const V_HUE: Record<string, string> = { "out-of-scope": "var(--danger)", borderline: "var(--warn)", "in-scope": "var(--ok)" };

export function ScopeSentinel({ p, canEdit, by }: { p: Project; canEdit: boolean; by: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const cur = p.billing?.currency ?? p.currency;
  const flags = (p.scopeFlags ?? []).filter((f) => !f.dismissed && f.verdict !== "in-scope");
  const chargeable = flags.filter((f) => !f.variationId);

  async function run() {
    setBusy(true); setErr(null);
    const requests = gatherRequests(p);
    if (!requests.length) { setBusy(false); setErr("No client requests to check yet."); return; }
    const res = await scopeCheck(agreedScope(p), requests, cur);
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The sentinel isn't switched on (needs an AI key)." : "Couldn't run the check."); return; }
    const now = new Date().toISOString();
    const prev = p.scopeFlags ?? [];
    const next: ScopeFlag[] = (res.flags ?? []).map((f) => {
      const req = requests[f.index];
      if (!req) return null;
      const key = `${req.source}:${req.ref ?? ""}:${req.text.slice(0, 60)}`;
      const existing = prev.find((x) => `${x.source}:${x.sourceRef ?? ""}:${x.request.slice(0, 60)}` === key);
      return {
        id: existing?.id ?? `sf-${Math.random().toString(36).slice(2, 9)}`,
        source: req.source, sourceRef: req.ref, request: req.text,
        verdict: f.verdict, reason: f.reason,
        suggestedTitle: f.suggestedTitle ?? undefined, suggestedAmount: f.suggestedAmount ?? undefined,
        flaggedOn: existing?.flaggedOn ?? now,
        dismissed: existing?.dismissed, variationId: existing?.variationId,
      } as ScopeFlag;
    }).filter(Boolean) as ScopeFlag[];
    actions.setScopeFlags(p.id, next);
    setOpen(true);
  }

  function raise(f: ScopeFlag) {
    const id = actions.createVariation(p.id, {
      title: f.suggestedTitle || f.request.slice(0, 60),
      description: f.request,
      reason: f.reason,
      amount: f.suggestedAmount ?? 0,
      sheetCodes: f.sourceRef ? [f.sourceRef] : undefined,
      by,
    });
    actions.linkScopeFlagVariation(p.id, f.id, id);
  }

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginTop: 22 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: flags.length || err ? "1px solid var(--line)" : "none", gap: 10 }}>
        <div className="row" style={{ gap: 8 }}>
          <Icon.bell width={16} style={{ color: "var(--accent-ink)" }} />
          <span className="kicker">Scope sentinel</span>
          {chargeable.length > 0 && <span className="badge" style={{ fontSize: 9, color: "var(--danger)" }}>{chargeable.length} chargeable</span>}
        </div>
        {canEdit && <button className="btn accent sm" disabled={busy} onClick={run}><Icon.spark width={13} />{busy ? "Checking…" : flags.length ? "Re-check" : "Check for extras"}</button>}
      </div>
      {err && <div style={{ padding: "12px 18px", color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
      {!err && flags.length === 0 && !busy && (
        <div className="dim" style={{ padding: "16px 18px", fontSize: 13, lineHeight: 1.6 }}>
          Reads every client change request, message and RFI against the agreed brief, services and exclusions — and tells you which ones are extras you should be billing.
        </div>
      )}
      {flags.length > 0 && (
        <div className="col" style={{ gap: 0 }}>
          {(open ? flags : flags.slice(0, 3)).map((f, i) => (
            <div key={f.id} style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
              <div className="row between wrap" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 8, minWidth: 0 }}>
                  <span className="badge" style={{ fontSize: 8.5, color: V_HUE[f.verdict] }}>{f.verdict}</span>
                  {f.sourceRef && <span className="mono dim" style={{ fontSize: 11 }}>{f.sourceRef}</span>}
                  <span className="dim" style={{ fontSize: 11 }}>{f.source} · {relative(f.flaggedOn)}</span>
                </span>
                {f.suggestedAmount ? <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>~{money(f.suggestedAmount, cur)}</span> : null}
              </div>
              <div style={{ fontSize: 13.5, marginTop: 5, lineHeight: 1.5 }}>{f.request.slice(0, 220)}</div>
              <div className="dim" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{f.reason}</div>
              {canEdit && (
                <div className="row wrap" style={{ gap: 8, marginTop: 9 }}>
                  {f.variationId
                    ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Variation raised</span>
                    : <button className="btn accent sm" onClick={() => raise(f)}><Icon.plus width={12} />Raise as variation</button>}
                  <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.dismissScopeFlag(p.id, f.id)}>Not chargeable</button>
                </div>
              )}
            </div>
          ))}
          {flags.length > 3 && <button className="btn ghost sm" style={{ margin: "10px 18px 14px" }} onClick={() => setOpen((v) => !v)}>{open ? "Show fewer" : `Show all ${flags.length}`}</button>}
        </div>
      )}
    </div>
  );
}
